import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { posts, getPost } from "@/data/blog";

const SITE = "https://netforgesols.com";

const Layout = ({ children, testid }) => (
  <div data-testid={testid} className="bg-[#0a0f1e] min-h-screen">
    <Navbar />
    <main className="pt-20">{children}</main>
    <Footer />
  </div>
);

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export function BlogList() {
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "NetForge Blog",
    url: `${SITE}/blog`,
    description:
      "Insights on IT, custom software, CRM systems, hardware, marketing, SEO and design from the NetForge team.",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE}/blog/${p.slug}`,
      datePublished: p.date,
      author: { "@type": "Organization", name: "NetForge" },
    })),
  };

  return (
    <>
      <Seo
        title="Blog — IT, CRM, Hardware & Marketing Insights | NetForge"
        description="Practical insights on custom software, CRM systems, IT hardware, cloud, digital marketing, SEO and branding — from the NetForge team."
        path="/blog"
      />
      <JsonLd id="blog" data={blogSchema} />
      <Layout testid="blog-page">
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#22d3ee] font-bold mb-4">
              The NetForge Blog
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-white">
              Ideas for building &amp; growing.
            </h1>
            <p className="mt-5 text-slate-400 leading-relaxed">
              Practical thinking on software, systems, hardware, and marketing —
              written by the team that builds them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <motion.article
                key={p.slug}
                data-testid={`blog-card-${p.slug}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="group flex flex-col bg-[#10182b] border border-white/10 rounded-xl overflow-hidden hover:border-[#22d3ee]/40 transition-colors"
              >
                <Link to={`/blog/${p.slug}`} className="block overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="h-48 w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </Link>
                <div className="flex flex-col flex-1 p-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#22d3ee] mb-3">
                    {p.category}
                  </span>
                  <h2 className="font-heading text-lg font-bold text-white leading-snug mb-3">
                    <Link to={`/blog/${p.slug}`} className="hover:text-[#22d3ee] transition-colors">
                      {p.title}
                    </Link>
                  </h2>
                  <p className="text-sm text-slate-400 leading-relaxed flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={13} /> {formatDate(p.date)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={13} /> {p.readTime}
                    </span>
                  </div>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-readmore-${p.slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#22d3ee] hover:gap-2.5 transition-all"
                  >
                    Read article <ArrowRight size={15} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      </Layout>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <Layout testid="blog-post-notfound">
        <section className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold text-white mb-4">
            Article not found
          </h1>
          <Link to="/blog" className="text-[#22d3ee] font-semibold hover:underline">
            Back to the blog
          </Link>
        </section>
      </Layout>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: `${SITE}/og-image.jpg`,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "NetForge", url: SITE },
    publisher: {
      "@type": "Organization",
      name: "NetForge",
      logo: { "@type": "ImageObject", url: `${SITE}/logo-footer.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${post.slug}` },
    keywords: post.tags.join(", "),
  };

  return (
    <>
      <Seo title={`${post.title} | NetForge`} description={post.excerpt} path={`/blog/${post.slug}`} />
      <JsonLd id="article" data={articleSchema} />
      <Layout testid="blog-post-page">
        <article className="max-w-3xl mx-auto px-6 md:px-8 py-14 md:py-20">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#22d3ee] transition-colors mb-8"
          >
            <ArrowLeft size={16} /> All articles
          </Link>

          <span className="text-xs font-bold uppercase tracking-wider text-[#22d3ee]">
            {post.category}
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-white mt-3 leading-tight">
            {post.title}
          </h1>
          <div className="mt-5 flex items-center gap-5 text-sm text-slate-500">
            <span>{post.author}</span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} /> {formatDate(post.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} /> {post.readTime}
            </span>
          </div>

          <img
            src={post.cover}
            alt={post.title}
            className="w-full h-64 sm:h-80 object-cover rounded-xl mt-8 border border-white/10"
          />

          <div className="mt-10 space-y-10">
            {post.content.map((section, i) => (
              <section key={i}>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  {section.heading}
                </h2>
                {section.body.map((para, j) => (
                  <p key={j} className="text-slate-300 leading-relaxed mb-4 text-[15px]">
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="text-xs font-medium text-slate-300 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full"
              >
                #{t}
              </span>
            ))}
          </div>

          <div className="mt-14 p-8 bg-[#10182b] border border-white/10 rounded-2xl text-center">
            <h3 className="font-heading text-xl font-bold text-white">
              Ready to build something?
            </h3>
            <p className="text-slate-400 mt-2 mb-6 text-sm">
              Let&apos;s talk about your project — the first consultation is free.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a0f1e] font-bold px-7 py-3 rounded-sm hover:bg-[#67e8f9] transition-colors"
            >
              Get in touch <ArrowRight size={16} />
            </Link>
          </div>
        </article>
      </Layout>
    </>
  );
}
